import { MouseEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import SecondaryEmailInput from 'ichraDecisionTool/common/SecondaryEmailInput'
import * as Yup from 'yup'

import { ProspectForSave, SaveProspectRequest, getProspectForSave } from 'core'

import {
  Button,
  DefaultCheckbox as CheckBox,
  Col,
  DefaultInputV2,
  Modal,
  Option,
  Row,
  DefaultSelect as Select,
  Tooltip,
} from 'common/components'
import { useQueryRequestV2 } from 'common/hooks'

interface ProspectFormProps {
  prospectId: string | null
  visible: boolean
  submitting: boolean
  onCancel: (e: MouseEvent<HTMLElement>) => void
  onSubmit: (values: SaveProspectRequest) => void | Promise<any>
}

const ProspectForm = ({ prospectId, visible, onCancel, onSubmit, submitting }: ProspectFormProps) => {
  const { t: translate } = useTranslation('ichra-decision-tool')

  const { data: prospect } = useQueryRequestV2<ProspectForSave>({
    initialState: null,
    queryFn: () => getProspectForSave(prospectId ?? ''),
  })

  const [formIsValid, setFormIsValid] = useState(false)
  const formRef = useRef<HTMLFormElement | null>(null)

  const SaveButtonWithTooltip = () =>
    !formIsValid ? (
      <Tooltip title="Complete all the fields to continue" position="right">
        <SaveButton />
      </Tooltip>
    ) : (
      <SaveButton />
    )

  const SaveButton = () => (
    <Button
      loading={submitting}
      disabled={!formIsValid}
      type="primary"
      onMouseUp={() => formRef!.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))}
    >
      {translate('save')}
    </Button>
  )

  const formFooter = (
    <div>
      <Button className={'mr-8'} onClick={onCancel}>
        {translate('cancel')}
      </Button>
      <SaveButtonWithTooltip />
    </div>
  )

  const removeSecondaryEmail = (
    secondaryEmails: string[],
    index: number,
    setFieldValues: (key: string, value: string[]) => void,
  ) =>
    setFieldValues(
      'secondaryEmails',
      secondaryEmails.length === 1 ? [] : [...secondaryEmails.slice(0, index), ...secondaryEmails.slice(index + 1)],
    )

  return (
    prospect && (
      <Modal onCancel={onCancel} visible={visible} title={translate('save-prospect.title')} footer={formFooter}>
        <Formik
          enableReinitialize={true}
          validationSchema={formValidator(translate)}
          initialValues={prospect}
          onSubmit={onSubmit}
          validate={(values: ProspectForSave) => {
            setFormIsValid(formValidator(translate).isValidSync(values))
            return {}
          }}
        >
          {({ setFieldValue, values: { secondaryEmails }, ...formik }) => (
            <form ref={formRef} onSubmit={formik.handleSubmit} autoComplete="off">
              <Row gutter={16}>
                <Col span={24}>
                  <DefaultInputV2 label={translate('save-prospect.company-name')} name="companyName" />
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <DefaultInputV2 label={translate('save-prospect.first-name')} name="firstName" />
                </Col>

                <Col span={12}>
                  <DefaultInputV2 label={translate('save-prospect.last-name')} name="lastName" />
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <DefaultInputV2 label={translate('save-prospect.email')} name="email" />
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <DefaultInputV2 label={translate('save-prospect.job-title')} name="jobTitle" />
                </Col>
              </Row>

              <Row>
                <Col>
                  {secondaryEmails.map((email, index) => (
                    <SecondaryEmailInput
                      translate={translate}
                      email={email}
                      index={index}
                      onRemoveButtonClick={(index) => removeSecondaryEmail(secondaryEmails, index, setFieldValue)}
                      onChange={(fn) => setFieldValue('secondaryEmails', secondaryEmails.map(fn))}
                    />
                  ))}
                </Col>
              </Row>

              <Row>
                <Col>
                  <div className="mb-16">
                    <Button
                      type="default"
                      icon="far fa-plus"
                      onClick={() => setFieldValue('secondaryEmails', [...secondaryEmails, ''])}
                    >
                      {translate('save-prospect.add-secondary-email')}
                    </Button>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Select
                    name="expirationDate"
                    defaultValue={[prospect.expirationDate]}
                    label={translate('save-prospect.expiration-date')}
                    labelTooltip="Lorem ipsum"
                  >
                    {prospect.availableExpirationDates.map(({ id, name }) => (
                      <Option key={id} value={id}>
                        {name}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              <Row>
                <Col>
                  <CheckBox
                    children={translate('save-prospect.is-ale')}
                    name="isAle"
                    className="no-select"
                    defaultChecked={prospect?.isAle ?? false}
                  />
                </Col>
              </Row>
            </form>
          )}
        </Formik>
      </Modal>
    )
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    firstName: Yup.string().trim().nullable().required(translate('save-prospect.required')),
    lastName: Yup.string().trim().nullable().required(translate('save-prospect.required')),
    companyName: Yup.string().trim().nullable().required(translate('save-prospect.required')),
    jobTitle: Yup.string().trim().nullable().required(translate('save-prospect.required')),
    email: Yup.string()
      .trim()
      .nullable()
      .email(translate('save-prospect.email-validation'))
      .required(translate('save-prospect.required')),
    expirationDate: Yup.number().required(translate('save-prospect.required')),
    secondaryEmails: Yup.array()
      .notRequired()
      .of(Yup.string().required().trim().email(translate('save-prospect.email-validation'))),
  })

export default ProspectForm
